import { type ProviderDriverKind, type ProviderInstanceId } from "@t3tools/contracts";
import { resolveSelectableModel } from "@t3tools/shared/model";

import { isProviderInstancePickerReady, type ProviderInstanceEntry } from "../../providerInstances";
import { getTriggerDisplayModelName, type ModelEsque } from "./providerIconUtils";

export function formatComposerModelLabel(model: ModelEsque): string {
  const displayName = getTriggerDisplayModelName(model).trim();
  const subProvider = model.subProvider?.trim();
  return (subProvider ? `${subProvider}/${displayName}` : displayName).toLowerCase();
}

export function deriveComposerModelPickerState(input: {
  activeInstanceId: ProviderInstanceId;
  instanceEntries: ReadonlyArray<ProviderInstanceEntry>;
  lockedProvider: ProviderDriverKind | null;
  lockedContinuationGroupKey: string | null;
  model: string;
  modelOptionsByInstance: ReadonlyMap<ProviderInstanceId, ReadonlyArray<ModelEsque>>;
}): {
  activeEntry: ProviderInstanceEntry | null;
  options: ReadonlyArray<ModelEsque>;
  selectedModel: ModelEsque | null;
} {
  const activeEntry =
    input.instanceEntries.find((entry) => entry.instanceId === input.activeInstanceId) ?? null;
  const matchesLock =
    activeEntry !== null &&
    (input.lockedProvider === null || activeEntry.driverKind === input.lockedProvider) &&
    (input.lockedContinuationGroupKey === null ||
      activeEntry.continuationGroupKey === input.lockedContinuationGroupKey);
  const options =
    activeEntry && matchesLock && isProviderInstancePickerReady(activeEntry)
      ? (input.modelOptionsByInstance.get(activeEntry.instanceId) ?? [])
      : [];
  const selectedModel = options.find((option) => option.slug === input.model) ?? options[0] ?? null;

  return { activeEntry, options, selectedModel };
}

export function commitComposerModelSelection(input: {
  activeEntry: ProviderInstanceEntry | null;
  options: ReadonlyArray<ModelEsque>;
  modelSlug: string;
  disabledReason: string | null;
  onInstanceModelChange: (instanceId: ProviderInstanceId, model: string) => void;
  onClose: () => void;
}): boolean {
  if (!input.activeEntry || input.disabledReason) {
    return false;
  }
  const resolvedModel = resolveSelectableModel(
    input.activeEntry.driverKind,
    input.modelSlug,
    input.options,
  );
  if (!resolvedModel) {
    return false;
  }

  input.onInstanceModelChange(input.activeEntry.instanceId, resolvedModel);
  input.onClose();
  return true;
}
